import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '~/app/auth/context/AuthContext';
import { useWorkOrderBoard } from '~/app/hooks/maintenance-execution/useWorkOrderBoard';
import { useWorkOrderKanbanLookups } from '~/app/components/maintenance/work-orders/WorkOrderKanbanPageContainer/hooks/useWorkOrderKanbanLookups';
import { OPTIMIZER_PAGE_SIZE } from '~/app/components/maintenance/work-orders/optimizer/constants';
import { useOptimizerMergeSuggestions } from '~/app/components/maintenance/work-orders/optimizer/hooks/useOptimizerMergeSuggestions';
import type { OptimizerWorkOrderLabel } from '~/app/components/maintenance/work-orders/optimizer/types';

export function useWorkOrderOptimizerPage() {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const [searchParams, setSearchParams] = useSearchParams();
  const siteFromQuery = searchParams.get('siteId') ?? '';
  const [siteFilter, setSiteFilterState] = useState(siteFromQuery);

  const setSiteFilter = (siteId: string) => {
    setSiteFilterState(siteId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (siteId) next.set('siteId', siteId);
        else next.delete('siteId');
        return next;
      },
      { replace: true },
    );
  };

  /** Prefer ?siteId= when opening via “View all” from Kanban. */
  const effectiveSiteFilter = siteFromQuery || siteFilter;

  const boardQuery = useWorkOrderBoard(organizationId);
  const lookups = useWorkOrderKanbanLookups(organizationId);

  const workOrderById = useMemo(() => {
    const map = new Map<string, OptimizerWorkOrderLabel>();
    for (const card of boardQuery.data?.workOrders ?? []) {
      map.set(card.id, {
        id: card.id,
        code: card.code,
        status: card.status,
      });
    }
    return map;
  }, [boardQuery.data?.workOrders]);

  const list = useOptimizerMergeSuggestions({
    organizationId,
    siteFilter: effectiveSiteFilter,
    pageSize: OPTIMIZER_PAGE_SIZE,
    workOrderById,
    siteNameById: lookups.siteNameById,
  });

  return {
    organizationId,
    siteFilter: effectiveSiteFilter,
    setSiteFilter,
    siteFilterOptions: lookups.siteFilterOptions,
    isLoadingBoard: boardQuery.isLoading || lookups.isLoadingLookups,
    list,
  };
}
